import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import type { PlaceResult } from '@poi-planner/shared';
import { usePlacesSearch } from '../hooks/usePlacesSearch';

interface PlacesSearchInputProps {
  onSelect: (place: PlaceResult) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function PlacesSearchInput({
  onSelect,
  placeholder = 'Search for a place...',
  disabled = false,
}: PlacesSearchInputProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, loading, error, search, clear } = usePlacesSearch();

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Open dropdown when results arrive
  useEffect(() => {
    if (results.length > 0) {
      setIsOpen(true);
      setFocusedIndex(-1);
    }
  }, [results]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (value.trim()) {
      search(value);
    } else {
      clear();
      setIsOpen(false);
    }
  };

  const handleSelect = (place: PlaceResult) => {
    onSelect(place);
    setQuery('');
    clear();
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < results.length) {
          handleSelect(results[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            'w-full p-3 text-[13px] font-sans rounded-lg border border-border text-text-primary outline-none transition-colors box-border',
            loading && 'pr-10',
            disabled ? 'bg-slate-100' : 'bg-white hover:border-text-muted focus:border-text-primary'
          )}
        />

        {/* Loading spinner */}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 size-4 flex items-center justify-center">
            <div className="size-3.5 border-2 border-border border-t-text-secondary rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-1.5 px-2.5 py-2 text-xs text-danger bg-red-50 rounded-md flex items-center gap-1.5">
          <span className="material-icons text-sm">error_outline</span>
          {error}
        </div>
      )}

      {/* Dropdown results */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 max-h-[280px] overflow-y-auto bg-white rounded-[10px] border border-border shadow-[0_10px_40px_rgba(0,0,0,0.12),0_4px_12px_rgba(0,0,0,0.08)] z-[1000]">
          {results.map((place, index) => (
            <div
              key={place.placeId}
              onClick={() => handleSelect(place)}
              onMouseEnter={() => setFocusedIndex(index)}
              className={clsx(
                'px-3.5 py-3 cursor-pointer transition-colors',
                focusedIndex === index ? 'bg-slate-50' : 'bg-transparent',
                index < results.length - 1 && 'border-b border-border'
              )}
            >
              <div className="flex items-start gap-2.5">
                <span className="material-icons text-lg text-text-muted mt-px shrink-0">
                  place
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-text-primary truncate">
                    {place.name}
                  </div>
                  <div className="text-xs text-text-muted mt-0.5 truncate">
                    {place.address}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
